import {ItemActionMenu, type ItemMenuAction} from '@github-ui/list-item-action-menu'
import {testIdProps} from '@github-ui/test-id-props'
import type {Icon} from '@primer/octicons-react'
import {clsx} from 'clsx'
import {type ReactNode, type RefObject, useEffect} from 'react'

import {useListViewItems} from '../ListView/ItemsContext'
import type {StylableProps} from '../types'
import styles from './ActionBar.module.css'

export type ListItemActionBarProps = StylableProps & {
  /**
   * Description of the controls, e.g. "Issue actions". Used for the menu toggle's accessible name.
   */
  label?: string
  /**
   * Actions shown in the item's menu.
   */
  staticMenuActions?: ItemMenuAction[]
  /**
   * Override the icon used in the button that opens the action menu. Defaults to KebabHorizontalIcon.
   */
  anchorIcon?: Icon
  /**
   * Element the menu should be anchored to, when it shouldn't be the toggle itself.
   */
  anchorRef?: RefObject<HTMLElement | null>
  /**
   * Controls rendered inline, before the menu.
   */
  children?: ReactNode
}

export const ListItemActionBar = ({
  anchorIcon,
  style,
  className,
  label = 'list item action bar',
  staticMenuActions,
  anchorRef,
  children,
}: ListItemActionBarProps) => {
  const {setAnyItemsWithActionBar, setHasResizableActionsWithActionBar} = useListViewItems()
  const hasInlineActions = Boolean(children)

  useEffect(() => setAnyItemsWithActionBar(true), [setAnyItemsWithActionBar])
  useEffect(() => {
    if (hasInlineActions) setHasResizableActionsWithActionBar(true)
  }, [hasInlineActions, setHasResizableActionsWithActionBar])

  return (
    <div
      className={clsx(styles.container, hasInlineActions && styles.hasActions, className)}
      style={style}
      {...testIdProps('list-view-item-action-bar-container')}
    >
      {children}
      <ItemActionMenu
        label={label}
        actions={staticMenuActions ?? []}
        anchorRef={anchorRef}
        toggleProps={anchorIcon ? {icon: anchorIcon} : undefined}
      />
    </div>
  )
}
